import React, { useState, useEffect } from 'react';
import { uploadFileToS3 } from 'util/helpers';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Spin,
  Alert,
  Typography,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import {
  useGenerateTextTo3DMutation,
  useCheckGenerationStatusQuery,
  useCreateOneGeneratedAssetsMutation,
} from '@src/components/generated/graphql';
import styles from './3d-generation.module.css';
import { useSession } from 'next-auth/react';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

type GLBSceneObject = {
  name: string;
  url: string;
};

export interface ShutterstockGenerationProps {
  addToScene: (data: any) => void;
}

const PREVIEW_FALLBACK_IMAGE =
  'https://d23hwblhugcea5.cloudfront.net/studioAssets/images/img1.jpg';

const ShutterstockGeneration: React.FC<ShutterstockGenerationProps> = ({
  addToScene,
}) => {
  const [form] = Form.useForm();
  const session = useSession();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [glbSceneObject, setGlbSceneObject] = useState<GLBSceneObject | null>(
    null
  );
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [createGeneratedAsset] = useCreateOneGeneratedAssetsMutation();
  const [generateTextTo3D, { loading: generatingModel }] =
    useGenerateTextTo3DMutation({
      onCompleted: (data) => {
        if (data?.generateTextTo3D?.id) {
          setGenerationId(data.generateTextTo3D.id);
          setIsPolling(true);
        } else if (data?.generateTextTo3D?.error) {
          setError(data.generateTextTo3D.error);
        }
      },
      onError: (error) => {
        setError(`Error initiating generation: ${error.message}`);
      },
    });

  async function uploadFileToStorage(url: string, fileName: string) {
    const blob = await fetch(url).then((res) => {
      if (res.status === 200) {
        return res.blob();
      }
      return null;
    });

    if (!blob) {
      return null;
    }

    const file = new File([blob], fileName);

    return await uploadFileToS3(
      file,
      session.data?.user?.email ?? 'no-email',
      '3d-generation'
    );
  }

  const { loading: checkingStatus, refetch: checkStatus } =
    useCheckGenerationStatusQuery({
      fetchPolicy: 'network-only',
      onCompleted: async (data) => {
        const statusData = data?.checkGenerationStatus;

        if (statusData?.status === 'completed') {
          setResult(statusData);
          setIsPolling(false);

          const glb = statusData?.output?.find(
            (output: any) => output.file_type === 'glb'
          );

          if (!glb) {
            return;
          }

          const fileName = `shutterstock-${
            statusData.id
          }-${new Date().toUTCString()}.glb`;

          const assetUrl = await uploadFileToStorage(glb.url, fileName);
          const userId = session.data?.user?.id;

          if (!assetUrl) {
            setError(`Generation failed`);
            setIsPolling(false);
            return;
          }

          if (userId) {
            const shutter3dPreview = statusData?.output?.find(
              (output: any) => output.type === 'preview_image'
            );

            const fileNamePreview = `shutterstock-${
              statusData.id
            }-${new Date().toUTCString()}-preview-image.png`;

            const previewUrl = shutter3dPreview
              ? await uploadFileToStorage(shutter3dPreview.url, fileNamePreview)
              : PREVIEW_FALLBACK_IMAGE;

            await createGeneratedAsset({
              variables: {
                data: {
                  assetUrl,
                  previewUrl: previewUrl ?? PREVIEW_FALLBACK_IMAGE,
                  userId,
                },
              },
            });
          }

          const newGlbSceneObject = {
            url: assetUrl,
            name: fileName,
          };

          setGlbSceneObject(newGlbSceneObject);
        } else if (statusData?.status === 'failed') {
          setError(`Generation failed: ${statusData.error || 'Unknown error'}`);
          setIsPolling(false);
        }
      },
    });

  useEffect(() => {
    if (!isPolling || !generationId) return () => {};

    const pollInterval = 10000;
    const poll = () => {
      checkStatus({
        checkGenerationStatusId: generationId,
      });
    };

    poll();

    const intervalId = setInterval(poll, pollInterval);

    return () => clearInterval(intervalId);
  }, [isPolling, generationId, checkStatus]);

  const handleSubmit = (values: any) => {
    setResult(null);
    setError(null);
    setGenerationId(null);
    setGlbSceneObject(null);

    const options: any = {};

    if (values.seed) {
      options.seed = parseInt(values.seed, 10);
    }

    if (values.outputFormat && values.outputFormat.length > 0) {
      options.output_format_list = values.outputFormat;
    }

    if (values.textureRes) {
      options.texture_res_num = parseInt(values.textureRes, 10);
    }

    if (values.turntableFormat) {
      options.turntable_format = values.turntableFormat;
    }

    if (values.mode) {
      options.mode = values.mode;
    }

    if (values.negativePrompt) {
      options.negative_prompt = values.negativePrompt;
    }

    if (values.mesh_adaptive_num_faces !== undefined) {
      options.mesh_adaptive_num_faces = values.mesh_adaptive_num_faces;
    }

    if (values.mesh_retopo_form) {
      options.mesh_retopo_form = values.mesh_retopo_form;
    }

    if (values.prompt_is_upsampled !== undefined) {
      options.prompt_is_upsampled = values.prompt_is_upsampled;
    }

    if (values.target_face_count && !values.mesh_adaptive_num_faces) {
      options.target_face_count = parseInt(values.target_face_count, 10);
    }

    generateTextTo3D({
      variables: {
        input: {
          prompt: values.prompt,
          options: options,
        },
      },
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  const addToSceneObject = () => {
    if (!glbSceneObject) return;
    addToScene({
      name: glbSceneObject?.name,
      url: glbSceneObject?.url,
      type: SIDE_MENU_EVENTS.LIBRARY_3D,
      index: 0,
    });
  };

  const renderResults = () => {
    if (!result) return null;

    const outputsByType: Record<string, any[]> = {};

    result.output?.forEach((output: any) => {
      if (!outputsByType[output.type]) {
        outputsByType[output.type] = [];
      }
      outputsByType[output.type].push(output);
    });

    return (
      <div className={styles.result}>
        <Title level={4}>Generation Results</Title>

        {outputsByType.preview_image && outputsByType.preview_image.length > 0 && (
          <>
            <div className={styles.resultTitle}>Preview Images:</div>
            {outputsByType.preview_image.map((preview: any, index: number) => (
              <div key={`preview-${index}`}>
                <img
                  src={preview.url}
                  alt={`Preview ${index + 1}`}
                  className={styles.previewImage}
                  onClick={addToSceneObject}
                />
              </div>
            ))}
          </>
        )}

        {outputsByType.turntable && outputsByType.turntable.length > 0 && (
          <>
            <div className={styles.resultTitle}>Turntable Preview:</div>
            {outputsByType.turntable.map((turntable: any, index: number) => (
              <div key={`turntable-${index}`}>
                {turntable.file_type === 'gif' ? (
                  <img
                    src={turntable.url}
                    alt='Turntable GIF'
                    className={styles.previewImage}
                    onClick={addToSceneObject}
                  />
                ) : turntable.file_type === 'mp4' ? (
                  <video
                    src={turntable.url}
                    controls
                    autoPlay
                    loop
                    muted
                    style={{
                      maxWidth: '100%',
                      borderRadius: '4px',
                      marginBottom: '16px',
                    }}
                  />
                ) : null}
              </div>
            ))}
          </>
        )}

        {[
          ...(outputsByType.model_file || []),
          ...(outputsByType.output_archive || []),
        ].length > 0 && (
          <>
            <div className={styles.resultTitle}>Download 3D Models:</div>
            {[
              ...(outputsByType.model_file || []),
              ...(outputsByType.output_archive || []),
            ].map((asset: any, index: number) => (
              <a
                key={`asset-${index}`}
                href={asset.url}
                target='_blank'
                rel='noopener noreferrer'
                className={styles.downloadLink}
              >
                Download {asset.file_type.toUpperCase()} file
              </a>
            ))}
          </>
        )}
        {glbSceneObject ? (
          <div className={styles.addButton}>
            <Button type='primary' onClick={addToSceneObject}>
              Add to Scene
            </Button>
          </div>
        ) : (
          <div className={styles.addButton}>
            <Spin />
            <span>Loading 3D model (you can add it to the scene later)</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.generationContainer}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={{
          prompt:
            'a lazy boy chair, lots of cushion, reclined with no one sitting on it',
          outputFormat: ['glb'],
          textureRes: 2048,
          turntableFormat: 'gif',
          mode: 'full',
          mesh_adaptive_num_faces: true,
          mesh_retopo_form: 'quad',
          prompt_is_upsampled: true,
          target_face_count: 20000,
        }}
      >
        <Form.Item
          name='prompt'
          label='Text Prompt'
          rules={[{ required: true, message: 'Please enter a text prompt' }]}
          className={styles.formItem}
        >
          <TextArea
            placeholder='Describe the 3D model you want to generate (max 400 words)'
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        </Form.Item>

        <div className={styles.optionsToggle} onClick={toggleAdvancedOptions}>
          {showAdvancedOptions ? (
            <>
              <UpOutlined /> Hide Advanced Options
            </>
          ) : (
            <>
              <DownOutlined /> Show Advanced Options
            </>
          )}
        </div>

        {showAdvancedOptions && (
          <div className={styles.optionsContainer}>
            <Form.Item
              name='seed'
              label='Seed Value'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Random seed if left empty'
                min={0}
                max={2147483648}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='outputFormat'
              label='Output Format'
              className={styles.formItem}
            >
              <Select mode='multiple' placeholder='Select output formats'>
                <Option value='glb'>GLB</Option>
                <Option value='obj'>OBJ</Option>
                <Option value='usdz'>USDZ</Option>
                <Option value='ply'>PLY</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='textureRes'
              label='Texture Resolution'
              className={styles.formItem}
            >
              <Select>
                <Option value={1024}>1024</Option>
                <Option value={2048}>2048</Option>
                <Option value={4096}>4096</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='turntableFormat'
              label='Turntable Format'
              className={styles.formItem}
            >
              <Select>
                <Option value='gif'>GIF</Option>
                <Option value='mp4'>MP4</Option>
                <Option value='none'>None</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='mode'
              label='Generation Mode'
              className={styles.formItem}
            >
              <Select>
                <Option value='full'>Full</Option>
                <Option value='preview'>Preview</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='negativePrompt'
              label='Negative Prompt'
              className={styles.formItem}
            >
              <Input placeholder='Terms to exclude (max 400 words)' />
            </Form.Item>

            <Form.Item
              name='mesh_adaptive_num_faces'
              label='Adaptive Mesh Faces'
              className={styles.formItem}
            >
              <Select>
                <Option value={true}>Enabled</Option>
                <Option value={false}>Disabled</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='mesh_retopo_form'
              label='Mesh Retopology Form'
              className={styles.formItem}
            >
              <Select>
                <Option value='quad'>Quad</Option>
                <Option value='triangle'>Triangle</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='prompt_is_upsampled'
              label='Upsample Prompt'
              className={styles.formItem}
            >
              <Select>
                <Option value={true}>Enabled</Option>
                <Option value={false}>Disabled</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='target_face_count'
              label='Target Face Count'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Target face count'
                min={500}
                max={200000}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
        )}

        <Form.Item>
          <Button
            type='primary'
            htmlType='submit'
            loading={generatingModel || (isPolling && checkingStatus)}
          >
            Generate 3D Model
          </Button>
        </Form.Item>
      </Form>

      {(generatingModel || isPolling) && (
        <div className={styles.loading}>
          <Spin />
          <div style={{ marginTop: 12 }}>
            <Text strong>
              {generatingModel
                ? 'Initiating 3D model generation...'
                : 'Generating 3D model...'}
            </Text>
            <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
              This process may take several minutes to complete.
            </div>
          </div>
        </div>
      )}

      {error && (
        <Alert
          message='Error'
          description={error}
          type='error'
          showIcon
          className={styles.errorAlert}
        />
      )}

      {renderResults()}
    </div>
  );
};

export default ShutterstockGeneration;
